import { Box } from "@mui/material";

import { useCallback, useEffect, useMemo, useState } from "react";
import { createUseStyles } from "react-jss";
import { theme } from "$utils/theme/bataillog";
import Text from "$components/Text";
import { Reception } from "$types";
import useReception from "$hooks/useReception";
import { Popin } from "$components/Popin/Popin";
import ReferencingForm from "$containers/Receptions/Referencing/ReferencingForm";
import JTooltip from "../JTooltip/JTooltip";
import { QTY_WIDTH } from "./JReceptionTree";
import BlueTextButton from "./BlueTextButton";
import { ReceptionService } from "$services";

type Props = {
  disabledMessage?: string;
  onEdit: () => Promise<void> | void;
  reception: Reception;
  isReferencingIncomplete: boolean;
};
function ReceptionLabel({
  disabledMessage,
  onEdit,
  reception,
  isReferencingIncomplete,
}: Props) {
  const classes = useStyles();
  const {
    referencingOpen,
    toggleReferencingPopin,
    focusedReceptionReferenceId,
  } = useReception();
  const { reference, orders } = reception;
  const orderPlural = orders.length > 1 ? "s" : "";
  // Use the reception prop directly since it already has all the nested data from /edb endpoint
  // No need to fetch individually - the /edb endpoint returns the same structure as findOne
  const fullReception = reception;

  const [shouldRefreshAfterClose, setShouldRefreshAfterClose] = useState(false);
  const focusKey = useMemo(
    () => -Math.abs(reception?.id ?? 0),
    [reception?.id]
  );
  const isCurrentPopin = focusedReceptionReferenceId === focusKey;

  const handleOnSubmit = useCallback(async (data: any) => {
    await ReceptionService.updateReceptionItem(data.id, data);
    setShouldRefreshAfterClose(true);
  }, []);

  useEffect(() => {
    if (!referencingOpen && shouldRefreshAfterClose) {
      setShouldRefreshAfterClose(false);
      if (onEdit) {
        onEdit();
      }
    }
  }, [onEdit, referencingOpen, shouldRefreshAfterClose]);

  return (
    <>
      <Box display="flex" alignItems="center">
        <JTooltip title={disabledMessage}>
          <Text>
            <Text type="important">{reference}</Text> (Commande{orderPlural}{" "}
            {orders
              .map((o) => o.reference)
              .map((ref, i) => (
                <Text key={ref}>
                  <b>{ref}</b>
                  {i !== orders.length - 1 ? " - " : ")"}
                </Text>
              ))}
          </Text>
        </JTooltip>
        {isReferencingIncomplete && !!fullReception && (
          <>
            <Box flex={1} textAlign="right">
              <BlueTextButton
                onClick={() => toggleReferencingPopin(true, focusKey)}
              >
                Ref. incomplet
              </BlueTextButton>
            </Box>
            <div className={classes.emptyRightBlock} />
          </>
        )}
      </Box>

      <Popin openPopin={referencingOpen} toggleDrawer={toggleReferencingPopin}>
        {isCurrentPopin && (
          <>
            <Text type="h2">Référencement</Text>
            <ReferencingForm
              isSavingReception={false}
              onSubmit={handleOnSubmit}
              receptionId={reception.id}
              togglePopin={toggleReferencingPopin}
            />
          </>
        )}
      </Popin>
    </>
  );
}

export default ReceptionLabel;

const useStyles = createUseStyles(() => ({
  emptyRightBlock: {
    width: `calc(${QTY_WIDTH} - ${theme.spacing(1)}px)`, // align with qty / unit below
  },
}));
